import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ResearchBlockModal from './ResearchBlockModal';
import { ProjectAppLoadingProvider } from '../../../providers/ProjectAppViewLoadingProvider';
// import ProjectAppTodoListBlock from './ProjectAppTodoListBlock';
import ProjectAppResearchSection from './ProjectAppResearchSection';
import ProjectPlanChecklist from './ProjectPlanChecklist';
import AgentStatusPill from './AgentStatusPill';
import BookingSections from './BookingSections';
import { useStatusPillHidden } from '../../../contexts/StatusPillContext';
import { usePusher } from '../../../contexts/PusherContext';
import api from '../../../api';
import {
  AssistantTodoItem,
  SubThreadUpdateEvent,
  AgentStreamEvents,
} from '@dreamai/types';
import { ReactComponent as TodoSpinnerIcon } from '../../../icons/todo-spinner.svg';
import { ReactComponent as HourglassIcon } from '../../../icons/hourglass.svg';
import { ReactComponent as AlertSquareIcon } from '../../../icons/alert-square.svg';
import { ReactComponent as DocumentTextIcon } from '../../../icons/document-text.svg';
import { ReactComponent as CheckmarkIcon } from '../../../icons/checkmark.svg';
import { ReactComponent as ChevronLeftIcon } from '../../../icons/chevron-left.svg';
import './project-app-blocks.scss';

// ─── Task status helpers (mirrored from AgentTasksList) ──────────

interface StepDetail {
  at: string;
  note: string;
}

interface StepLike {
  id?: string;
  description: string;
  status: string;
  active?: boolean;
  details?: StepDetail[];
}

const localizeTimestamp = (iso: string): string => {
  const target = new Date(iso);
  if (isNaN(target.getTime())) return iso;
  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  const absMs = Math.abs(diffMs);
  const timeStr = target.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  if (diffMs > 0 && absMs < 24 * 60 * 60 * 1000) return timeStr;
  if (diffMs > 0 && absMs < 7 * 24 * 60 * 60 * 1000) {
    const dayName = target.toLocaleDateString(undefined, { weekday: 'long' });
    return `${dayName} at ${timeStr}`;
  }
  return (
    target.toLocaleDateString(undefined, { day: 'numeric', month: 'long' }) +
    ` at ${timeStr}`
  );
};

const formatEta = (eta: string): string | null => {
  if (!eta) return null;
  if (eta.includes('{timestamp}')) {
    return eta.replace(/\{timestamp\}([^{]+)\{\/timestamp\}/g, (_, iso) =>
      localizeTimestamp(iso.trim()),
    );
  }
  const target = new Date(eta);
  if (!isNaN(target.getTime())) return localizeTimestamp(eta);
  return eta;
};

const getLatestNote = (step?: StepLike): string | null => {
  if (!step?.details || step.details.length === 0) return null;
  const last = step.details[step.details.length - 1];
  return last?.note?.trim() || null;
};

const getActiveStep = (todo: AssistantTodoItem): StepLike | undefined => {
  const steps = (todo as any).steps || [];
  return (
    steps.find((s: StepLike) => s.active) ||
    steps.find((s: StepLike) => s.status === 'in_progress') ||
    steps.find((s: StepLike) => s.status === 'waiting_on_user') ||
    steps.find((s: StepLike) => s.status === 'waiting_on_third_party') ||
    steps.find((s: StepLike) => s.status === 'failed') ||
    steps.find((s: StepLike) => s.status === 'pending')
  );
};

const resolveDisplayStatus = (
  todo: AssistantTodoItem,
  activeStep?: StepLike,
): 'complete' | 'failed' | 'waiting' | 'processing' => {
  const stepStatus = activeStep?.status || todo.status;
  if (stepStatus === 'complete') return 'complete';
  if (stepStatus === 'failed' || stepStatus === 'cancelled_by_user')
    return 'failed';
  if (
    stepStatus === 'waiting_on_user' ||
    stepStatus === 'waiting_on_third_party'
  )
    return 'waiting';
  return 'processing';
};

const getStepIcon = (displayStatus: string) => {
  if (displayStatus === 'complete')
    return (
      <svg
        className='step-icon complete'
        xmlns='http://www.w3.org/2000/svg'
        width='16'
        height='16'
        viewBox='0 0 16 16'
        fill='none'
      >
        <circle cx='8' cy='8' r='6' stroke='#489FD9' strokeWidth='4' />
      </svg>
    );
  if (displayStatus === 'failed')
    return <AlertSquareIcon className='step-icon failed' />;
  if (displayStatus === 'waiting')
    return <HourglassIcon className='step-icon hourglass' />;
  return <TodoSpinnerIcon className='step-icon spinner' />;
};

// ─── TaskStatusCard ──────────────────────────────────────────────

const TaskStatusCard: React.FC<{ threadId: string }> = ({ threadId }) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherChannel = usePusher();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [todo, setTodo] = useState<AssistantTodoItem | null>(null);
  const [openDocAppId, setOpenDocAppId] = useState<string | null>(null);
  const [openDocTitle, setOpenDocTitle] = useState<string | undefined>(
    undefined,
  );

  const fetchTodo = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        success: boolean;
        todos: AssistantTodoItem[];
      }>('/todos', { headers: { Authorization: `Bearer ${token}` } });
      const match = res.data.todos?.find((t) => t.id === threadId);
      setTodo(match || null);
    } catch (err) {
      console.error('[TaskStatusCard] Error fetching todo:', err);
    }
  }, [threadId, getAccessTokenSilently]);

  useEffect(() => {
    fetchTodo();
  }, [fetchTodo]);

  useEffect(() => {
    if (!pusherChannel) return;
    const handleUpdate = (data: SubThreadUpdateEvent) => {
      if (data.subThread?.id !== threadId) return;
      setTodo((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          status: data.subThread.status as any,
          summary: data.subThread.summary,
          steps: data.subThread.steps as any,
          completionEta: data.subThread.completionEta,
        };
      });
    };
    pusherChannel.bind(AgentStreamEvents.SUB_THREAD_UPDATE, handleUpdate);
    return () => {
      pusherChannel.unbind(AgentStreamEvents.SUB_THREAD_UPDATE, handleUpdate);
    };
  }, [pusherChannel, threadId]);

  const documents: Array<{
    id: string;
    title?: string;
    projectAppId?: string;
  }> = (todo as any)?.informationDumps || [];

  // Auto-open research document from URL param (e.g. navigated from dashboard)
  useEffect(() => {
    const researchAppId = searchParams.get('research_app_id');
    if (researchAppId && documents.length > 0 && !openDocAppId) {
      const match = documents.find((d) => d.projectAppId === researchAppId);
      if (match?.projectAppId) {
        setOpenDocAppId(match.projectAppId);
        setOpenDocTitle(match.title);
      } else {
        setOpenDocAppId(researchAppId);
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('research_app_id');
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, documents.length, openDocAppId, setSearchParams]);

  if (!todo) return null;

  const steps: StepLike[] = (todo as any).steps || [];
  const activeStep = getActiveStep(todo);
  const displayStatus = resolveDisplayStatus(todo, activeStep);
  const isTerminalStatus =
    displayStatus === 'complete' || displayStatus === 'failed';
  const stepText = isTerminalStatus
    ? todo.summary || activeStep?.description || 'Done'
    : activeStep?.description || todo.summary || 'Working on it...';
  const completedSteps = steps.filter((s) => s.status === 'complete').length;
  // Overall task ETA, formatted to the user's local time. Hidden on terminal
  // statuses since by then there's nothing left to wait for.
  const etaText =
    !isTerminalStatus && (todo as any).completionEta
      ? formatEta((todo as any).completionEta)
      : null;

  const showBreadcrumb =
    todo.parentThreadId &&
    !todo.parentThreadIsInternalMonologue &&
    !todo.parentThreadIsRehomedTask &&
    !(todo as any).parentThreadIsTaskParent;

  return (
    <>
      {showBreadcrumb && (
        <button
          className='task-breadcrumb'
          onClick={() =>
            navigate(`/conversations?thread_id=${todo.parentThreadId}`)
          }
        >
          <ChevronLeftIcon />
          <span>{todo.parentThreadDescription}</span>
        </button>
      )}

      {/* Task status card — single-line status like AgentTasksList,
          plus the overall Task ETA. Per-step notes are shown in the
          Task Steps list below, not here. */}
      <div className='task-status-card'>
        <h3 className='task-status-card-heading'>Task status</h3>
        <div className='task-status-step'>
          {getStepIcon(displayStatus)}
          <span className='task-status-step-label'>{stepText}</span>
        </div>
        {etaText && (
          <div className='task-status-eta'>
            <span className='task-status-eta-label'>Task ETA</span>
            <span className='task-status-eta-value'>{etaText}</span>
          </div>
        )}
      </div>

      {/* Task steps — project-plan-checklist style. Each step shows the
          most recent detail note underneath the title (when present) so the
          user can see WHAT actually happened in each step, not just that it
          ran. This is the per-step audit trail. */}
      {steps.length > 0 && (
        <div className='task-status-card'>
          <div className='task-steps-header'>
            <h3 className='task-status-card-heading'>Task Steps</h3>
            <span className='task-steps-count'>
              {completedSteps}/{steps.length}
            </span>
          </div>
          <ul className='task-steps-list'>
            {steps.map((step: StepLike, idx: number) => {
              const isDone = step.status === 'complete';
              const isProcessing = step.status === 'in_progress' || step.active;
              const stepNote = getLatestNote(step);
              return (
                <li
                  key={step.id || idx}
                  className={`task-step-item${isDone ? ' done' : ''}${isProcessing ? ' processing' : ''}`}
                >
                  <span
                    className={`plan-check${isDone ? ' complete' : isProcessing ? ' processing' : ' empty'}`}
                  >
                    {isDone ? (
                      <CheckmarkIcon />
                    ) : isProcessing ? (
                      <TodoSpinnerIcon className='plan-check-spinner' />
                    ) : null}
                  </span>
                  <div className='task-step-body'>
                    <span className='task-step-text'>{step.description}</span>
                    {stepNote && (
                      <span className='task-step-note'>{stepNote}</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Documents — same style as Project Research */}
      {documents.length > 0 && (
        <div className='project-app-research-section'>
          <h2 className='project-app-block-heading project-app-block-heading--h2'>
            Documents
          </h2>
          <div className='project-app-research-cards'>
            {documents.map((doc) => (
              <div
                key={doc.id}
                className='project-app-research-card'
                role='button'
                tabIndex={0}
                onClick={() => {
                  if (doc.projectAppId) {
                    setOpenDocAppId(doc.projectAppId);
                    setOpenDocTitle(doc.title);
                  }
                }}
              >
                <div className='research-card-top'>
                  <DocumentTextIcon />
                </div>
                <div className='research-card-bottom'>
                  {doc.title || 'Research Document'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {openDocAppId && (
        <ProjectAppLoadingProvider>
          <ResearchBlockModal
            show={!!openDocAppId}
            onClose={() => {
              setOpenDocAppId(null);
              setOpenDocTitle(undefined);
            }}
            appId={openDocAppId}
            title={openDocTitle}
            parentThreadId={threadId}
            backLabel={todo.description}
          />
        </ProjectAppLoadingProvider>
      )}
    </>
  );
};

// ─── ProjectPlanView ─────────────────────────────────────────────

interface ProjectPlanViewProps {
  threadId: string | null;
  discussingObject?: string;
  threadDescription?: string;
}

const ProjectPlanView: React.FC<ProjectPlanViewProps> = ({
  threadId,
  discussingObject,
  threadDescription,
}) => {
  const isBooking = discussingObject?.toLowerCase() === 'booking';
  const isTask = useStatusPillHidden();

  return (
    <div className='project-app-page'>
      {isBooking ? (
        <BookingSections threadId={threadId} />
      ) : isTask && threadId ? (
        <div className='project-app-top-section'>
          <TaskStatusCard threadId={threadId} />
          <ProjectAppResearchSection
            threadId={threadId}
            threadName={threadDescription}
          />
        </div>
      ) : (
        <>
          {/* <div className='project-app-todo-lists'>
            <ProjectAppTodoListBlock
              variant='waiting_on_user'
              threadId={threadId}
            />
            <ProjectAppTodoListBlock variant='other' threadId={threadId} />
          </div> */}
          <div className='project-app-top-section'>
            {threadId && (
              <div className='project-plan-status-pill-row'>
                {threadDescription && <span>{threadDescription}</span>}
                <AgentStatusPill threadId={threadId} />
              </div>
            )}
            <ProjectAppResearchSection
              threadId={threadId}
              threadName={threadDescription}
            />
          </div>
          <ProjectPlanChecklist
            threadId={threadId}
            threadName={threadDescription}
          />
        </>
      )}
    </div>
  );
};

export default ProjectPlanView;
